import { Component, ViewEncapsulation, OnInit, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { trigger, style, transition, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { DepositModalService } from 'src/app/services/deposit-modal/deposit-modal.service';
import { DepositService } from 'src/app/services/deposit/deposit.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { ClientAccountModalService } from 'src/app/services/client-account-modal/client-account-modal.service';

// Define filter interface
interface DepositFilters {
  keyword?: string;
  email?: string;
  payment?: string;
  received_date_from?: string;
  received_date_to?: string;
}

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-manage-deposits-list',
  templateUrl: './manage-deposits-list.component.html',
  styleUrls: ['./manage-deposits-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ManageDepositsListComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  deposits: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;

  // Filter properties
  showFilters: boolean = false;
  filters: DepositFilters = {};
  constructor(
    private _depositModalService: DepositModalService,
    private _depositService: DepositService,
    private _clientAccountModalService: ClientAccountModalService,
    private router: Router
  ) {
    super();
   }

  ngOnInit(){
    this.GetData();
    this.setupModalSubscriptions();
  }

  private setupModalSubscriptions(): void {
    // Subscribe to deposit modal service changes if needed
    this.subscriptions.push(
      this._depositModalService.selectedDeposit$.subscribe(deposit => {
        // This subscription helps track modal state changes
        console.log('Modal deposit changed:', deposit);
      })
    );
  }

  ngAfterViewInit(): void {
    // Add event listeners for popup positioning
    setTimeout(() => {
    }, 100);

    // Initialize KTMenu instances
    this.initializeMenus();
  }

  // Helper method for tracking items in ngFor
  trackByDepositId(index: number, deposit: any): any {
    return deposit._id || index;
  }

  openAddDepositModal(): void {
    // Clear any selected deposit (for add mode)
    this._depositModalService.clearSelectedDeposit();

    // Open the modal
    const modalElement = document.getElementById('kt_modal_add_update_deposit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditDepositModal(deposit: any): void {
    // Set the selected deposit for edit mode
    this._depositModalService.setSelectedDeposit(deposit);

    // Open the modal
    const modalElement = document.getElementById('kt_modal_add_update_deposit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onDepositAdded(): void {
    console.log('onDepositAdded called - Deposit added successfully');
    // Refresh the data
    this.GetData();
  }

  onDepositUpdated(): void {
    console.log('onDepositUpdated called - Deposit updated successfully');
    // Refresh the data
    this.GetData();
  }

  navigateToAllocation(customerId: string): void {
    this.router.navigate(['/sales/sales-payment/allocation', customerId]);
  }

  GetData(page: number = this.currentPage, search: string = this.searchTerm, filters: DepositFilters = this.filters){
    this.isLoading = true;
    this.currentPage = page;

    // Build filters object
    const apiFilters: any = {};

    if (filters.keyword) {
      apiFilters.keyword = filters.keyword;
    }
    if (filters.email) {
      apiFilters.email = filters.email;
    }
    if (filters.payment && filters.payment !== 'All') {
      apiFilters.payment = filters.payment;
    }
    if (filters.received_date_from) {
      apiFilters.received_date_from = filters.received_date_from;
    }
    if (filters.received_date_to) {
      apiFilters.received_date_to = filters.received_date_to;
    }

    this.subscriptions.push(
      this._depositService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          console.log('API Response:', response); // Debug log
          if (response.status && response.data) {
            this.deposits = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            console.log('Deposits set:', this.deposits); // Debug log
            console.log('Pagination info:', this.paginationInfo); // Debug log
          } else {
            this.deposits = [];
            this.resetPagination();
            console.log('No data found in response'); // Debug log
          }
          this.isLoading = false;

          // Initialize menus after data loads
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading deposits:', error);
          this.deposits = [];
          this.resetPagination();
          this.isLoading = false;
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
        console.log('KTMenu instances initialized for deposits list');
      } else {
        console.warn('KTMenu is not available');
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
        console.log('KTMenu instances refreshed for deposits list');
      } else {
        console.warn('KTMenu is not available for refresh');
      }
    }, 50);
  }

  openClientAccountModal(customerId: string): void {
    console.log('Opening client account modal for customer:', customerId);

    if (!customerId) {
      console.error('No customer ID provided');
      return;
    }

    // Set the customer ID in the service
    this._clientAccountModalService.setSelectedCustomerId(customerId);

    // Small delay to ensure data is set
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_manage_deposits_client_account_modal');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Pagination method
  onPageChange(page: number): void {
    this.GetData(page, this.searchTerm, this.filters);
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    console.log('Applied filters:', this.filters);
    this.currentPage = 1;
    this.GetData();
  }

  resetFilters(): void {
    this.filters = {};
    this.searchTerm = '';
    this.showFilters = false;
    this.currentPage = 1;
    this.GetData();
  }

}


